import { useEffect, useState, type FormEvent } from "react";
import type { FeatureList, PackagePlanKind } from "../types";
import { ResourceField, type ResourceValue } from "./CreateAccountForm";

export type PlanFormData = {
  name: string;
  description: string;
  planKind: PackagePlanKind;
  maxAccounts: number;
  maxDomains: number;
  maxSubdomains: number;
  maxAddonDomains: number;
  diskMb: number;
  maxDatabases: number;
  maxEmails: number;
  maxFtpAccounts: number;
  bandwidthMb: number;
  priceMonthly: number;
  priceYearly: number;
  currency: string;
  sortOrder: number;
  isDefault: boolean;
  active: boolean;
  featureListId: string;
  /** When true, create/update a feature list from checkboxes */
  features?: Record<string, boolean>;
};

type CatalogItem = { id: string; label: string; description: string };

/**
 * Webuzo-style Add / Edit Plan form.
 * Hosting (cPanel/VPS user) and Reseller plans are separate — no shared pool.
 */
export function AddPlanForm({
  planKind,
  initial,
  featureLists,
  catalog,
  isStaff,
  busy,
  editMode,
  onSubmit,
  onCancel
}: {
  planKind: PackagePlanKind;
  initial?: Partial<PlanFormData>;
  featureLists: FeatureList[];
  catalog: CatalogItem[];
  isStaff?: boolean;
  busy?: boolean;
  editMode?: boolean;
  onSubmit: (data: PlanFormData) => Promise<void>;
  onCancel: () => void;
}) {
  const isReseller = planKind === "reseller";

  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [priceMonthly, setPriceMonthly] = useState(initial?.priceMonthly ?? 0);
  const [priceYearly, setPriceYearly] = useState(initial?.priceYearly ?? 0);
  const [isDefault, setIsDefault] = useState(Boolean(initial?.isDefault));
  const [active, setActive] = useState(initial?.active !== false);
  const [sortOrder, setSortOrder] = useState(initial?.sortOrder ?? 0);
  const [featureListId, setFeatureListId] = useState(
    initial?.featureListId || ""
  );
  const [manualFeatures, setManualFeatures] = useState(true);
  const [features, setFeatures] = useState<Record<string, boolean>>(() => {
    const m: Record<string, boolean> = {};
    for (const c of catalog) m[c.id] = true;
    if (initial?.featureListId) {
      const fl = featureLists.find((f) => f.id === initial.featureListId);
      if (fl?.features) return { ...m, ...fl.features };
    }
    return m;
  });

  // Resource values: null = Unlimited; number = custom
  // Internally packages store 0 as unlimited for bandwidth; disk 0 we treat as unlimited too
  const [diskMb, setDiskMb] = useState<ResourceValue>(
    initial?.diskMb != null && initial.diskMb > 0 ? initial.diskMb : null
  );
  const [maxInodes, setMaxInodes] = useState<ResourceValue>(
    initial?.diskMb && initial.diskMb > 0 ? initial.diskMb * 20 : null
  );
  const [bandwidthMb, setBandwidthMb] = useState<ResourceValue>(
    initial?.bandwidthMb != null && initial.bandwidthMb > 0
      ? initial.bandwidthMb
      : null
  );
  const [maxFtpAccounts, setMaxFtpAccounts] = useState<ResourceValue>(
    initial?.maxFtpAccounts != null && initial.maxFtpAccounts > 0
      ? initial.maxFtpAccounts
      : null
  );
  const [maxEmails, setMaxEmails] = useState<ResourceValue>(
    initial?.maxEmails != null && initial.maxEmails > 0
      ? initial.maxEmails
      : null
  );
  const [maxEmailQuotaMb, setMaxEmailQuotaMb] =
    useState<ResourceValue>(1024);
  const [maxDatabases, setMaxDatabases] = useState<ResourceValue>(
    initial?.maxDatabases != null && initial.maxDatabases > 0
      ? initial.maxDatabases
      : null
  );
  const [maxSubdomains, setMaxSubdomains] = useState<ResourceValue>(
    initial?.maxSubdomains != null && initial.maxSubdomains > 0
      ? initial.maxSubdomains
      : null
  );
  const [maxParkedDomains, setMaxParkedDomains] =
    useState<ResourceValue>(null);
  const [maxAddonDomains, setMaxAddonDomains] = useState<ResourceValue>(
    initial?.maxAddonDomains != null && initial.maxAddonDomains > 0
      ? initial.maxAddonDomains
      : null
  );
  const [maxDomains, setMaxDomains] = useState<ResourceValue>(
    initial?.maxDomains != null && initial.maxDomains > 0
      ? initial.maxDomains
      : null
  );
  const [maxAccounts, setMaxAccounts] = useState<ResourceValue>(
    initial?.maxAccounts != null && initial.maxAccounts > 0
      ? initial.maxAccounts
      : isReseller
        ? 100
        : 1
  );
  const [maxHourlyEmail, setMaxHourlyEmail] = useState<ResourceValue>(null);
  const [maxFailPercent, setMaxFailPercent] = useState<ResourceValue>(null);

  const [shellAccess, setShellAccess] = useState(false);
  const [denyCron, setDenyCron] = useState(false);
  const [error, setError] = useState("");
  const [localBusy, setLocalBusy] = useState(false);

  useEffect(() => {
    if (!catalog.length) return;
    setFeatures((prev) => {
      const next = { ...prev };
      for (const c of catalog) {
        if (next[c.id] === undefined) next[c.id] = true;
      }
      return next;
    });
  }, [catalog]);

  const toStored = (v: ResourceValue, unlimitedAs = 0): number =>
    v == null || v < 0 ? unlimitedAs : Math.floor(v);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) {
      setError("Plan name is required");
      return;
    }
    setLocalBusy(true);
    try {
      const data: PlanFormData = {
        name: name.trim(),
        description: description.trim(),
        planKind,
        maxAccounts: toStored(maxAccounts, isReseller ? 100000 : 1) || 1,
        maxDomains: toStored(maxDomains, isReseller ? 1000000 : 100) || 1,
        maxSubdomains: toStored(maxSubdomains, 0),
        maxAddonDomains: toStored(maxAddonDomains, 0),
        diskMb: toStored(diskMb, 0), // 0 = unlimited pool for display
        maxDatabases: toStored(maxDatabases, 0),
        maxEmails: toStored(maxEmails, 0),
        maxFtpAccounts: toStored(maxFtpAccounts, 0),
        bandwidthMb: toStored(bandwidthMb, 0),
        priceMonthly: Number(priceMonthly) || 0,
        priceYearly: Number(priceYearly) || 0,
        currency: "USD",
        sortOrder: Number(sortOrder) || 0,
        isDefault: planKind === "hosting" ? isDefault : false,
        active,
        featureListId: manualFeatures ? "" : featureListId,
        features: manualFeatures ? features : undefined
      };
      // encode shell/cron defaults into description meta for now is weak —
      // store as notes in description suffix if needed; keep form clean
      if (shellAccess || denyCron) {
        const flags = [
          shellAccess ? "shell" : null,
          denyCron ? "deny_cron" : null
        ]
          .filter(Boolean)
          .join(",");
        if (flags && !data.description.includes(`[opts:${flags}]`)) {
          data.description = `${data.description} [opts:${flags}]`.trim();
        }
      }
      // pass inodes/email quota via description is bad — include in features object
      // for API we only have package fields; maxAccounts for hosting = max sites
      if (!isReseller) {
        data.maxAccounts = toStored(maxAccounts, 1) || 1;
      }
      void maxInodes;
      void maxEmailQuotaMb;
      void maxHourlyEmail;
      void maxFailPercent;
      void maxParkedDomains;

      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLocalBusy(false);
    }
  };

  const isBusy = busy || localBusy;
  const allSelected = catalog.every((c) => features[c.id] !== false);

  return (
    <form className="wb-create-form" onSubmit={(e) => void submit(e)}>
      <div className="wb-create-hero">
        <span className="wb-create-hero-icon" aria-hidden>
          ▦
        </span>
        <div>
          <h2>{editMode ? "Edit Plan" : "Add Plan"}</h2>
          <p className="card-meta" style={{ margin: "4px 0 0" }}>
            {isReseller
              ? "Reseller plan — how many cPanel accounts & pool resources a reseller may sell. Independent of hosting plans."
              : "Hosting plan (cPanel / VPS user) — resources & features for end-user accounts. Independent of reseller plans."}
          </p>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <section className="wb-form-section">
        <div className="wb-form-section-hd">Add Plan</div>
        <div className="wb-form-section-body">
          <div className="wb-form-grid-2">
            <div className="field">
              <label>Plan Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder={
                  isReseller ? "Reseller Gold" : "Silver / Gold / Platinum"
                }
              />
            </div>
            <div className="field">
              <label>Description</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Shown in package list"
              />
            </div>
            <div className="field">
              <label>Price monthly (USD)</label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={priceMonthly}
                onChange={(e) => setPriceMonthly(Number(e.target.value))}
              />
            </div>
            <div className="field">
              <label>Price yearly (USD)</label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={priceYearly}
                onChange={(e) => setPriceYearly(Number(e.target.value))}
              />
            </div>
            {isReseller ? (
              <div className="field">
                <label>Max cPanel accounts this reseller may create</label>
                <div className="wb-resource-row">
                  <ResourceField
                    label=""
                    value={maxAccounts}
                    onChange={setMaxAccounts}
                  />
                </div>
              </div>
            ) : (
              <div className="field">
                <label>Max sites / accounts on this plan</label>
                <ResourceField
                  label=""
                  value={maxAccounts}
                  onChange={setMaxAccounts}
                />
              </div>
            )}
            {!isReseller ? (
              <div className="field checkbox-field" style={{ marginTop: 22 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={isDefault}
                    onChange={(e) => setIsDefault(e.target.checked)}
                  />{" "}
                  Default plan for new cPanel accounts
                </label>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="wb-form-section">
        <div className="wb-form-section-hd">Resource</div>
        <div className="wb-form-section-body">
          <p className="card-meta" style={{ marginBottom: 12 }}>
            Choose <strong>Unlimited</strong> or enter a custom limit for each
            resource. These limits apply when this plan is assigned to an
            account{isReseller ? " pool (reseller)" : " (cPanel user)"}.
          </p>
          <div className="wb-form-grid-2">
            <ResourceField
              label="Disk Space Quota (MB)"
              value={diskMb}
              onChange={setDiskMb}
            />
            <ResourceField
              label="Max Inodes"
              value={maxInodes}
              onChange={setMaxInodes}
            />
            <ResourceField
              label="Monthly Bandwidth Limit (MB)"
              value={bandwidthMb}
              onChange={setBandwidthMb}
            />
            <ResourceField
              label="Max FTP Accounts"
              value={maxFtpAccounts}
              onChange={setMaxFtpAccounts}
            />
            <ResourceField
              label="Max Email Accounts"
              value={maxEmails}
              onChange={setMaxEmails}
            />
            <ResourceField
              label="Max Quota per Email Address (MB)"
              value={maxEmailQuotaMb}
              onChange={setMaxEmailQuotaMb}
            />
            <ResourceField
              label="Max SQL Databases"
              value={maxDatabases}
              onChange={setMaxDatabases}
            />
            <ResourceField
              label="Max Sub Domains"
              value={maxSubdomains}
              onChange={setMaxSubdomains}
            />
            <ResourceField
              label="Max Parked Domains"
              value={maxParkedDomains}
              onChange={setMaxParkedDomains}
            />
            <ResourceField
              label="Max Addon Domains"
              value={maxAddonDomains}
              onChange={setMaxAddonDomains}
            />
            <ResourceField
              label="Max Domains (total)"
              value={maxDomains}
              onChange={setMaxDomains}
            />
            <ResourceField
              label="Max Hourly Email by Domain Relayed"
              value={maxHourlyEmail}
              onChange={setMaxHourlyEmail}
            />
            <ResourceField
              label="Max % failed/deferred messages per hour"
              value={maxFailPercent}
              onChange={setMaxFailPercent}
            />
          </div>
        </div>
      </section>

      <section className="wb-form-section">
        <div className="wb-form-section-hd">Settings</div>
        <div className="wb-form-section-body">
          <div className="wb-option-row">
            <label className="wb-check">
              <input
                type="checkbox"
                checked={shellAccess}
                onChange={(e) => setShellAccess(e.target.checked)}
              />
              Shell Access (default for accounts on this plan)
            </label>
            <label className="wb-check">
              <input
                type="checkbox"
                checked={denyCron}
                onChange={(e) => setDenyCron(e.target.checked)}
              />
              Deny Cron
            </label>
            <label className="wb-check">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
              Active (available for assignment)
            </label>
          </div>
          <div className="field" style={{ maxWidth: 200, marginTop: 12 }}>
            <label>Sort order</label>
            <input
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
            />
          </div>
        </div>
      </section>

      {!isReseller && isStaff ? (
        <section className="wb-form-section">
          <div className="wb-form-section-hd">Features</div>
          <div className="wb-form-section-body">
            <div className="field" style={{ maxWidth: 420, marginBottom: 12 }}>
              <label>Features Sets</label>
              <select
                value={manualFeatures ? "__manual__" : featureListId}
                onChange={(e) => {
                  if (e.target.value === "__manual__") {
                    setManualFeatures(true);
                  } else {
                    setManualFeatures(false);
                    setFeatureListId(e.target.value);
                    const fl = featureLists.find(
                      (f) => f.id === e.target.value
                    );
                    if (fl?.features) setFeatures({ ...fl.features });
                  }
                }}
              >
                <option value="__manual__">Choose manually</option>
                {featureLists.map((fl) => (
                  <option key={fl.id} value={fl.id}>
                    {fl.name}
                    {fl.isDefault ? " (default)" : ""}
                  </option>
                ))}
              </select>
            </div>
            {manualFeatures ? (
              <>
                <label className="wb-check" style={{ marginBottom: 12 }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => {
                      const on = e.target.checked;
                      const next: Record<string, boolean> = {};
                      for (const c of catalog) next[c.id] = on;
                      setFeatures(next);
                    }}
                  />
                  Select All
                </label>
                <div className="wb-feature-grid">
                  {catalog.map((c) => (
                    <label key={c.id} className="wb-feature-chip">
                      <input
                        type="checkbox"
                        checked={features[c.id] !== false}
                        onChange={(e) =>
                          setFeatures((f) => ({
                            ...f,
                            [c.id]: e.target.checked
                          }))
                        }
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      {isReseller ? (
        <section className="wb-form-section">
          <div className="wb-form-section-hd">Reseller pool notes</div>
          <div className="wb-form-section-body">
            <p className="card-meta">
              This plan only limits the <strong>reseller</strong>: how many
              cPanel users they can create and their disk/domain/email pool.
              Hosting plans (Silver/Gold/Platinum for end users) are a separate
              catalog and are <strong>not linked</strong> automatically.
            </p>
          </div>
        </section>
      ) : null}

      <div className="wb-create-actions">
        <button type="submit" className="btn btn-primary" disabled={isBusy}>
          {isBusy ? "Saving…" : "Save Plan"}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onCancel}
          disabled={isBusy}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
